import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Sparkles, Menu, X, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/services", label: "Services" },
  { to: "/pricing", label: "Pricing" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
  { to: "/careers", label: "Careers" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const dashHref = user ? (
    user.role === "owner" ? "/owner" :
    user.role === "parent" ? "/parent" :
    user.role === "student" ? "/student" :
    (user.role === "cleaner" || user.role === "service_provider") ? "/cleaner" :
    "/admin"
  ) : "/login";

  return (
    <header className="sticky top-0 z-40 glass border-b border-border/60">
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
          <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center text-primary-foreground">
            <Sparkles size={18} strokeWidth={1.75} />
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold text-primary text-lg">QuadScrub</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Student Living Services</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "text-primary bg-primary/5" : "text-muted-foreground hover:text-primary hover:bg-muted"
                }`
              }
              end={n.to === "/"}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          {user ? (
            <>
              <Button asChild variant="ghost" size="sm" data-testid="goto-dashboard-btn">
                <Link to={dashHref}>My Dashboard</Link>
              </Button>
              <Button size="sm" variant="outline" onClick={async () => { await logout(); navigate("/"); }} data-testid="logout-btn">
                <LogOut size={14} /> Logout
              </Button>
            </>
          ) : (
            <>
              <Button asChild variant="ghost" size="sm" data-testid="login-btn">
                <Link to="/login">Sign In</Link>
              </Button>
              <Button asChild size="sm" className="bg-primary hover:bg-primary/90" data-testid="book-now-btn">
                <Link to="/booking">Book Now</Link>
              </Button>
            </>
          )}
        </div>

        <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border/60 bg-background">
          <div className="px-6 py-4 flex flex-col gap-2">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="py-2 text-sm font-medium">
                {n.label}
              </Link>
            ))}
            <div className="flex gap-2 mt-2">
              {user ? (
                <>
                  <Button asChild className="flex-1"><Link to={dashHref} onClick={() => setOpen(false)}>Dashboard</Link></Button>
                  <Button variant="outline" onClick={async () => { setOpen(false); await logout(); navigate("/"); }}>Logout</Button>
                </>
              ) : (
                <>
                  <Button asChild variant="outline" className="flex-1"><Link to="/login" onClick={() => setOpen(false)}>Sign In</Link></Button>
                  <Button asChild className="flex-1"><Link to="/booking" onClick={() => setOpen(false)}>Book Now</Link></Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
